import React, { useState, useEffect } from "react";
import fileDownload from "js-file-download";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";
import {
  getAllContractsRequested,
  setAllContractSelected,
  setContractFilter,
} from "../../../redux/actions/contracts";
import { setNotification } from "../../../helpers/notification";
import {
  getMarketMatchesRequested,
  setMarketMatchesBuyerOrganization,
  setMarketMatchesSellerOrganization,
} from "../../../redux/actions/offers";
import DataTable from "../../../components/DataTable";
import Pill from "../../../components/Pill";
import { Input } from "../../../components/Inputs";
import Button from "../../../components/Button";
import Loading from "../../../components/Loading/Loading";
import EmptyState from "../../../components/EmptyState/EmptyState";
import ComboBoxDynamic from "../../../components/ComboBoxDynamic/ComboBoxDynamic";
import ModalMatchHistory from "../../../components/Modal/ModalMatchHistory";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import ModalContractHistory from "../../../components/Modal/ModalContractHistory";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import MarketMatchModal from "../../../components/Modal/MarketMatchModal";
import MarketResultSubDetailsModal from "../../../components/Modal/MarketResultSubDetailsModal";
import CanceledMatchModal from "../../../components/Modal/CanceledMatchModal";
import { API_EXPORT } from "../../../helpers/api";

export default function OffersAndMatches({ t }) {
  const dispatch = useDispatch();
  const {
    all: contracts,
    total,
    loading: contractsLoading,
    allSelected: selectedContract,
    filter,
  } = useSelector((state) => state.contracts);

  const marketMatches = useSelector((state) => state.offers.marketMatches);

  const marketMatchesLoading = useSelector((state) => state.offers.loading);
  const filterBuyerOrganization = useSelector(
    (state) => state.offers.marketMatchesBuyerOrganization
  );

  const filterSellerOrganization = useSelector(
    (state) => state.offers.marketMatchesSellerOrganization
  );

  const cancalledMatchCertificate = useSelector(
    (state) => state.offers.cancalledMatchCertificate
  );

  const [editMarketMatch, setEditMarketMatch] = useState();

  const [inspectedContract, setInspectedContract] = useState();
  const [historyMatchToggle, setHistoryMatchToggle] = useState();
  const [inspectedCertificate, setInspectedCertificate] = useState();
  const [activePage, setActivePage] = useState(0);
  const [changeWindow, setChangeWindow] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    if (changeWindow) {
      if (
        cancalledMatchCertificate &&
        cancalledMatchCertificate?.status === 400
      ) {
        setNotification("error", cancalledMatchCertificate?.data?.message);
      } else {
        setNotification("success", cancalledMatchCertificate?.data?.message);
      }
    }
  }, [cancalledMatchCertificate]);

  const contractsColumns = [
    {
      name: t("pages.default.marketResults.table.contractName"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.table.bestBuyAmount"),
      selector: "bestBuyAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.bestBuyAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.bestSpread"),
      selector: "bestSpread",
      sortable: true,
      cell: (row) => {
        return row.bestSpread;
      },
    },
    {
      name: t("pages.default.marketResults.table.bestSellAmount"),
      selector: "bestSellAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.bestSellAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.maxMatchPrice"),
      selector: "maxMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.maxMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.minMatchPrice"),
      selector: "minMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.minMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.lastMatchAmount"),
      selector: "lastMatchAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.lastMatchAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.lastMatchPrice"),
      selector: "lastMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.lastMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.tradingVolume"),
      selector: "tradingVolume",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.tradingVolume);
      },
    },
    {
      name: t("pages.default.marketResults.table.weightedAverage"),
      selector: "weightedAverage",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.weightedAverage, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.status"),
      selector: "status",
      sortable: true,
      cell: (row) => {
        return row.status === "ACTIVE" ? (
          <Pill
            type="success"
            label={t("pages.default.offerOperations.active")}
            emphasis
          />
        ) : (
          <Pill
            type="important"
            label={t("pages.default.offerOperations.passive")}
            emphasis
          />
        );
      },
    },
    {
      name: t("pages.default.marketResults.table.inspect"),
      selector: "id",
      sortable: false,
      cell: (row) => {
        return (
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => setInspectedContract(row)}
          />
        );
      },
    },
  ];

  const marketMatchesColumns = [
    {
      name: t("pages.default.marketResults.buyerOrganization"),
      selector: "buyerOrganization",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.matchPrice"),
      selector: "primatchPricece",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.matchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.matchQuantity"),
      selector: "matchAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.matchAmount);
      },
    },
    {
      name: t("pages.default.marketResults.sellerOrganization"),
      selector: "sellOrganization",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.matchDate"),
      selector: "matchDate",
      sortable: true,
      cell: (row) => {
        return moment(row.matchDate).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      name: t("pages.default.marketResults.table.status"),
      selector: "status",
      sortable: true,
      cell: (row) => {
        return row.status.value === "VALID" ? (
          <Pill type="success" label={row.status.text} emphasis />
        ) : (
          <Pill type="important" label={row.status.text} emphasis />
        );
      },
    },
    {
      name: t("pages.default.marketResults.table.inspect"),
      selector: "id",
      sortable: false,
      cell: (row) => {
        return (
          <Button
            suffixIcon="search"
            onClick={() => {
              setInspectedCertificate(row);
            }}
          />
        );
      },
    },
    {
      name: t("pages.default.marketResults.table.transaction"),
      selector: "id",
      sortable: false,
      cell: (row) => {
        return (
          <Button
            suffixIcon="edit"
            disabled={row.status.value === "CANCELLED" || !row.cancelable}
            onClick={() => {
              setEditMarketMatch(row);
            }}
          />
        );
      },
    },
  ];
  // initial load of contracts
  useEffect(() => {
    requestContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedContract) {
        requestMarketMatches();
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract, filterBuyerOrganization, filterSellerOrganization]);

  const filterChange = (e) => {
    // deep copy
    let filterCopy = JSON.parse(JSON.stringify(filter));
    filterCopy = {
      ...filterCopy,
      [e.target.name]: e.target.value,
    };
    dispatch(setContractFilter(filterCopy));
  };

  const requestContracts = () => {
    dispatch(getAllContractsRequested());
  };
  const contractSelection = (row) => {
    dispatch(setAllContractSelected({ contract: row }));
  };
  const contractRowSelected = (row) => {
    if (selectedContract) return row.id === selectedContract.id;
    else return false;
  };
  const isHistoryMatchSelected = (row) => {
    if (historyMatchToggle) return row.id === historyMatchToggle.id;
    else return false;
  };
  const requestMarketMatches = () => {
    dispatch(
      getMarketMatchesRequested({
        contractId: selectedContract.id,
        buyOrganizationId: filterBuyerOrganization
          ? filterBuyerOrganization.organizationId
          : "",
        sellOrganizationId: filterSellerOrganization
          ? filterSellerOrganization.organizationId
          : "",
      })
    );
  };

  const downloadCertificates = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("exchange-order-match-admin/export/excel", {
        contractId: selectedContract?.id,
        buyOrganizationId: filterBuyerOrganization?.organizationId
          ? filterBuyerOrganization.organizationId
          : "" || null,
        sellOrganizationId: filterSellerOrganization?.organizationId
          ? filterSellerOrganization.organizationId
          : "" || null,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };

  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getAllContractsRequested({
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.offersAndMatches.title")}
      </h5>

      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.marketResults.contracts")}
          </p>
          <div className="km__p--market-results-contracts-header-filter">
            <Input
              type="month"
              placement="bottom-end"
              placeholder={t("pages.default.marketResults.sessionPeriod")}
              name="period"
              value={filter.period}
              onChange={filterChange}
            />
            <Button label={t("ui.default.filter")} onClick={requestContracts} />
          </div>
        </div>
        {/* Kontratlar */}
        {!contractsLoading && contracts.length > 0 && (
          <DataTableServerSideRendering
            columns={contractsColumns}
            data={contracts}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            rowSelection={(row) => contractSelection(row)}
            isRowSelected={(row) => contractRowSelected(row)}
            selectableRow
            activePage={activePage}
            totalPageCount={total?.totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {!contractsLoading && contracts.length === 0 && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.marketResults.contractNotFound")}
          />
        )}
        {contractsLoading && (
          <Loading text={t("pages.default.marketResults.contractLoading")} />
        )}
      </div>
      {/* Piyasa Eşleşmeleri */}
      {selectedContract ? (
        <div className="km__p--card">
          <div className="km__p--market-results-contracts-header">
            <p className="km__u--text_title">
              {t("pages.default.marketResults.marketMatches")} -{" "}
              {selectedContract.contractName}
            </p>
            <div className="km__p--market-results-contracts-header-filter">
              <ComboBoxDynamic
                inputPlaceholder={t(
                  "pages.default.marketResults.buyerOrganization"
                )}
                selectedOrganization={filterBuyerOrganization}
                organizationSelect={setMarketMatchesBuyerOrganization}
              />
              <ComboBoxDynamic
                inputPlaceholder={t(
                  "pages.default.marketResults.sellerOrganization"
                )}
                selectedOrganization={filterSellerOrganization}
                organizationSelect={setMarketMatchesSellerOrganization}
              />

              <Button
                label={t("ui.default.filter")}
                onClick={requestMarketMatches}
              />
              <Button
                type="secondary"
                label={t("ui.default.download")}
                suffixIcon="download"
                onClick={downloadCertificates}
                disabled={exportLoading}
                id="c-download"
              />
            </div>
          </div>
          {marketMatches.length > 0 && (
            <DataTable
              rowKey="id"
              pagination
              paginationPerPage={10}
              columns={marketMatchesColumns}
              data={marketMatches}
              selectableRow
              rowSelection={(row) => setHistoryMatchToggle(row)}
              isRowSelected={(row) => isHistoryMatchSelected(row)}
            />
          )}
          {!marketMatchesLoading && marketMatches.length === 0 && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.marketResults.notFound")}
            />
          )}
        </div>
      ) : null}
      {/* Kontrat işlem detayları */}
      {inspectedContract && (
        <ModalContractHistory
          isOpen={inspectedContract ? true : false}
          toggle={() => setInspectedContract()}
          contract={inspectedContract}
        />
      )}
      {/* Eşleşme Detayları */}
      {historyMatchToggle && (
        <ModalMatchHistory
          t={t}
          isOpen={historyMatchToggle ? true : false}
          toggle={() => setHistoryMatchToggle()}
          exchangeOrderMatch={historyMatchToggle}
        />
      )}
      {/* Transfered certificate */}
      {/* {inspectedCertificate && (
        <ModalCertificate
          isOpen={inspectedCertificate ? true : false}
          toggle={() => setInspectedCertificate()}
          certificateId={inspectedCertificate.transferedCertificateId}
          startNo={inspectedCertificate.documentStartNo}
          endNo={inspectedCertificate.documentEndNo}
          amount={inspectedCertificate.quantity}
        />
      )} */}
      {inspectedCertificate?.status?.value === "CANCELLED" ? (
        <CanceledMatchModal
          isOpen={inspectedCertificate ? true : false}
          toggle={() => setInspectedCertificate()}
          offerId={inspectedCertificate?.orderId}
          offerPrice={inspectedCertificate?.matchPrice}
          offerQuantity={inspectedCertificate?.quantity}
          matchDate={inspectedCertificate?.matchDate}
          price={inspectedCertificate?.price}
          quantity={inspectedCertificate?.matchAmount}
          contractName={inspectedCertificate?.contractName}
          matchStatusDescription={inspectedCertificate?.matchStatusDescription}
        />
      ) : (
        <>
          {inspectedCertificate &&
          inspectedCertificate?.matchList?.length === 1 ? (
            <ModalCertificate
              isOpen={inspectedCertificate?.matchList[0] ? true : false}
              toggle={() => setInspectedCertificate()}
              certificateId={
                inspectedCertificate?.matchList[0]?.transferedCertificateId
              }
              startNo={inspectedCertificate?.matchList[0]?.documentStartNo}
              endNo={inspectedCertificate?.matchList[0]?.documentEndNo}
              amount={inspectedCertificate?.matchList[0]?.quantity}
            />
          ) : (
            <MarketResultSubDetailsModal
              t={t}
              datas={inspectedCertificate}
              isOpen={inspectedCertificate?.matchList ? true : false}
              toggle={() => setInspectedCertificate()}
              certificateId={
                inspectedCertificate?.matchList[0]?.transferedCertificateId
              }
            />
          )}
        </>
      )}

      {/* Transfered certificate */}
      {editMarketMatch && (
        <MarketMatchModal
          isOpen={editMarketMatch ? true : false}
          toggle={() => setEditMarketMatch()}
          reqFonks={() => setChangeWindow(true)}
          marketMatchId={editMarketMatch.id}
          buyerOrganization={editMarketMatch.buyerOrganization}
          sellerOrganization={editMarketMatch.sellOrganization}
          matchDate={editMarketMatch.matchDate}
          price={editMarketMatch.matchPrice}
          quantity={editMarketMatch.matchAmount}
          contractName={editMarketMatch.contractName}
          matchList={editMarketMatch.matchList}
        />
      )}
    </>
  );
}
